/**
 * C10 employee-claims view-model — pure display/shaping helpers kept out of the
 * .vue so node:test can cover the logic directly (the companies repo has no SFC
 * test runner). The template just binds the output.
 *
 * Covers: §2 claim-status → .tag class mapping, per-status counts for the summary
 * cards, id→label maps (employee name / policy number resolved from the separate
 * /employees + /policies lists since a claim only carries employeeId + policyId),
 * and a plain-CSV export builder for the "تصدير" action (no export endpoint — the
 * list is serialized client-side).
 */

// S3 claim statuses in a fixed display order (matches the KPI cards + tags).
var STATUS_ORDER = ['registered', 'at_insurer', 'missing_doc', 'accepted', 'rejected'];

// status → .tag modifier class available in vivo-components.css.
var CLAIM_TAG = {
  registered: 'new',
  at_insurer: 'review',
  missing_doc: 'muted',
  accepted: 'ok',
  rejected: 'danger',
};

function claimTag(status) {
  return CLAIM_TAG[status] || 'muted';
}

/**
 * Build an id→value lookup from a list of entities (e.g. employees keyed to
 * fullName, policies keyed to number). Empty-safe.
 */
function buildMap(rows, valueField) {
  var map = {};
  var list = rows || [];
  for (var i = 0; i < list.length; i++) {
    var row = list[i];
    if (row && row.id != null) map[String(row.id)] = row[valueField];
  }
  return map;
}

/** Resolve a name from an id→name map, falling back to a readable "#id". */
function resolveName(map, id) {
  var key = String(id == null ? '' : id);
  if (map && map[key] != null && map[key] !== '') return map[key];
  return key ? '#' + key : '—';
}

/**
 * Per-status counts across the full claims list, always returning one entry per
 * §2 status (count 0 when absent) in STATUS_ORDER. Empty-safe.
 */
function statusCounts(claims) {
  var list = claims || [];
  var totals = {};
  for (var s = 0; s < STATUS_ORDER.length; s++) totals[STATUS_ORDER[s]] = 0;
  for (var i = 0; i < list.length; i++) {
    var st = list[i] && list[i].status;
    if (Object.prototype.hasOwnProperty.call(totals, st)) totals[st] += 1;
  }
  return STATUS_ORDER.map(function (status) {
    return { status: status, count: totals[status] };
  });
}

/**
 * Serialize the (already-resolved) claim rows to CSV for the export action.
 * `nameMap`/`policyMap` resolve the employeeId/policyId columns. Pure string out
 * so the .vue only wraps it in a Blob download.
 */
function toCsv(claims, nameMap, policyMap) {
  var list = claims || [];
  var header = ['id', 'employee', 'policy', 'serviceType', 'amount', 'invoiceDate', 'status'];
  var lines = [header.join(',')];
  for (var i = 0; i < list.length; i++) {
    var c = list[i] || {};
    var cells = [
      c.id,
      resolveName(nameMap, c.employeeId),
      resolveName(policyMap, c.policyId),
      c.serviceType,
      c.amount,
      c.invoiceDate,
      c.status,
    ];
    lines.push(
      cells
        .map(function (v) {
          var str = v == null ? '' : String(v);
          return '"' + str.replace(/"/g, '""') + '"';
        })
        .join(','),
    );
  }
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// HR create-claim form (POST /claims) -------------------------------------
// ---------------------------------------------------------------------------

function normalizeAmount(v) {
  return String(v == null ? '' : v).replace(/[٠-٩]/g, function (d) {
    return String('٠١٢٣٤٥٦٧٨٩'.indexOf(d));
  }).replace(/[,،\s]/g, '').replace('٫', '.');
}

/**
 * Validate the create-claim form. `today` is an ISO date (YYYY-MM-DD) injected so
 * tests are deterministic. Returns { field: errorKey } — empty object = valid.
 * errorKey is an i18n suffix under claims.create.errors.*.
 */
function validateClaimForm(form, today) {
  var f = form || {};
  var errors = {};
  if (!f.employeeId) errors.employeeId = 'employeeRequired';
  var amt = normalizeAmount(f.amount);
  if (!amt) errors.amount = 'amountRequired';
  else if (!/^\d+(\.\d{1,2})?$/.test(amt)) errors.amount = 'amountInvalid';
  else if (Number(amt) <= 0) errors.amount = 'amountPositive';
  else if (Number(amt) > 9999999999.99) errors.amount = 'amountTooLarge';
  var d = String(f.serviceDate || '');
  if (!d) errors.serviceDate = 'dateRequired';
  else if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || isNaN(Date.parse(d))) errors.serviceDate = 'dateInvalid';
  else if (today && d > today) errors.serviceDate = 'dateFuture';
  if (!String(f.provider || '').trim()) errors.provider = 'providerRequired';
  else if (String(f.provider).trim().length > 200) errors.provider = 'providerTooLong';
  if (String(f.note || '').length > 1000) errors.note = 'noteTooLong';
  return errors;
}

/**
 * POST /claims body (docs/CONTRACT-CHANGES.md): employeeId, amount, serviceDate,
 * providerName, optional note, optional documentIds (the employee's existing
 * MedicalDocument ids — HR has no upload/list endpoint for those, so the portal
 * only forwards ids when a caller supplies them). Kept in ONE place so a contract
 * tweak is a one-liner.
 */
function buildClaimPayload(form) {
  var f = form || {};
  var body = {
    employeeId: String(f.employeeId),
    amount: normalizeAmount(f.amount),
    serviceDate: f.serviceDate,
    providerName: String(f.provider || '').trim(),
  };
  if (f.policyId != null && String(f.policyId) !== '') body.policyId = String(f.policyId);
  var note = String(f.note || '').trim();
  if (note) body.note = note;
  if (f.documentIds && f.documentIds.length) body.documentIds = f.documentIds.map(String);
  return body;
}

var CLAIM_API_ERRORS = [
  'INVALID_AMOUNT',
  'INVALID_REFERENCE',
  'INVALID_SERVICE_DATE',
  'EMPLOYEE_NOT_FOUND',
  'EMPLOYEE_NOT_ACTIVE',
  'CLAIM_POLICY_REQUIRED',
  'POLICY_NOT_ACTIVE',
  'POLICY_NOT_FOUND',
  'SERVICE_DATE_OUT_OF_POLICY',
  'VALIDATION_ERROR',
  'FORBIDDEN',
  'NOT_FOUND',
];

/** Axios error -> i18n suffix under claims.create.apiErrors.* (never raw server text). */
function claimErrorKey(err) {
  var res = err && err.response;
  var code = res && res.data && res.data.code;
  if (code && CLAIM_API_ERRORS.indexOf(code) !== -1) return code;
  if (res && res.status === 403) return 'FORBIDDEN';
  if (res && res.status === 413) return 'PAYLOAD_TOO_LARGE';
  if (res && (res.status === 400 || res.status === 422)) return 'VALIDATION_ERROR';
  if (!res) return 'network';
  return 'generic';
}

// ---------------------------------------------------------------------------
// Claim detail (HR read-only) ---------------------------------------------
// ---------------------------------------------------------------------------

var REASON_FIELDS = ['decisionReason', 'reviewerNote', 'decisionNote', 'reviewNote', 'reason'];

/**
 * The reviewer's decision reason: an explicit field when the API has one, else the
 * note on the latest history entry that moved the claim into a decided/pending-docs
 * status. '' when none (the UI shows a dash — nothing is invented).
 */
function decisionReason(claim) {
  var c = claim || {};
  for (var i = 0; i < REASON_FIELDS.length; i++) {
    var v = c[REASON_FIELDS[i]];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  var hist = (c.history || c.statusHistory || []).slice().sort(function (a, b) {
    var av = (a && a.createdAt) || '';
    var bv = (b && b.createdAt) || '';
    return av < bv ? -1 : av > bv ? 1 : 0;
  });
  for (var j = hist.length - 1; j >= 0; j--) {
    var h = hist[j] || {};
    if ((h.toStatus === c.status || !c.status) && h.note && String(h.note).trim()) return String(h.note).trim();
  }
  return '';
}

/** Normalised documents: [{ id, kind, fileRef }] from claim.docs / claim.documents. */
function documentList(claim) {
  var raw = (claim && (claim.docs || claim.documents)) || [];
  return raw
    .map(function (d, i) {
      var doc = d || {};
      return { id: doc.id != null ? String(doc.id) : null, kind: doc.kind || '', fileRef: doc.fileRef || '', index: i + 1 };
    })
    .filter(function (d) {
      return d.id != null;
    });
}

/** Authorized file endpoint (bearer-authenticated; fetched as a blob, never a raw fileRef link). */
function documentFilePath(claimId, docId) {
  return '/claims/' + encodeURIComponent(String(claimId)) + '/documents/' + encodeURIComponent(String(docId)) + '/file';
}

/** HR may only follow claims — decisions belong to the insurer reviewer. */
function canDecide() {
  return false;
}

// ---------------------------------------------------------------------------
// Adjudication breakdown (GET /claims/:id -> adjudication, read-only) ---------
// ---------------------------------------------------------------------------

var RULE_CODES = ['DEDUCTIBLE_PCT', 'COPAY_PCT', 'BENEFIT_LIMIT', 'ANNUAL_CAP'];

function numOrNull(v) {
  if (v == null || v === '') return null;
  var n = Number(v);
  return isFinite(n) ? n : null;
}

/**
 * Display model of the stored breakdown. null when the claim has none yet (the
 * backend sends null until approved) so the UI shows a "not decided" note instead
 * of inventing numbers. decidedAmount falls back to claim.decidedAmount.
 * rules[].known tells the UI whether an i18n label exists for the code.
 */
function adjudicationView(claim) {
  var c = claim || {};
  var a = c.adjudication;
  if (!a || typeof a !== 'object') return null;
  var covered = numOrNull(a.coveredAmount);
  var amount = numOrNull(a.claimAmount);
  var share = numOrNull(a.employeeShare);
  if (share == null && amount != null && covered != null) share = Math.round((amount - covered) * 100) / 100;
  var decided = numOrNull(a.decidedAmount);
  if (decided == null) decided = numOrNull(c.decidedAmount);
  var rules = (Array.isArray(a.appliedRules) ? a.appliedRules : []).map(function (r) {
    var rule = r || {};
    return {
      code: String(rule.code || ''),
      known: RULE_CODES.indexOf(rule.code) !== -1,
      detail: rule.detail == null ? '' : String(rule.detail),
      before: numOrNull(rule.before),
      after: numOrNull(rule.after),
    };
  });
  return {
    claimAmount: amount,
    coveredAmount: covered,
    employeeShare: share,
    decidedAmount: decided,
    overriddenBelowCovered: !!a.overriddenBelowCovered,
    capRemaining: numOrNull(a.capRemaining),
    consumedToDate: numOrNull(a.consumedToDate),
    rules: rules,
  };
}

// ---------------------------------------------------------------------------
// Claim attachments (POST /claims/:id/documents, multipart `file`) ------------
// ---------------------------------------------------------------------------

var ATTACH_MAX_FILES = 10;
var ATTACH_MAX_BYTES = 10 * 1024 * 1024;
var ATTACH_EXT = ['pdf', 'png', 'jpg', 'jpeg'];
var ATTACH_KINDS = ['invoice', 'report', 'prescription', 'other'];

/**
 * Client-side pre-check of the picked files (the server re-validates magic bytes).
 * Returns { accepted: File[], errors: [{ name, key }] } with key an i18n suffix
 * under claims.create.attach.errors.*.
 */
function checkAttachments(files, existingCount) {
  var accepted = [];
  var errors = [];
  var list = files || [];
  for (var i = 0; i < list.length; i++) {
    var f = list[i] || {};
    var ext = String(f.name || '').split('.').pop().toLowerCase();
    if (ATTACH_EXT.indexOf(ext) === -1) errors.push({ name: f.name, key: 'type' });
    else if (!(f.size > 0)) errors.push({ name: f.name, key: 'empty' });
    else if (f.size > ATTACH_MAX_BYTES) errors.push({ name: f.name, key: 'size' });
    else if ((existingCount || 0) + accepted.length >= ATTACH_MAX_FILES) errors.push({ name: f.name, key: 'count' });
    else accepted.push(f);
  }
  return { accepted: accepted, errors: errors };
}

function uploadPath(claimId) {
  return '/claims/' + encodeURIComponent(String(claimId)) + '/documents';
}

/**
 * Upload each file sequentially (one multipart request per file, as the endpoint
 * takes a single `file`). `post(path, FormData)` and `makeForm(file, kind)` are
 * injected so the logic is node-testable. Never throws: returns
 * { uploaded: n, failed: [{ name, errorKey }] } so the (already created) claim is
 * never lost to a failed attachment.
 */
async function uploadAttachments(claimId, files, kind, post, makeForm) {
  var failed = [];
  var uploaded = 0;
  for (var i = 0; i < files.length; i++) {
    try {
      await post(uploadPath(claimId), makeForm(files[i], kind || 'other'));
      uploaded += 1;
    } catch (e) {
      failed.push({ name: files[i] && files[i].name, errorKey: attachErrorKey(e) });
    }
  }
  return { uploaded: uploaded, failed: failed };
}

/** Axios error -> i18n suffix under claims.create.attach.apiErrors.*. */
function attachErrorKey(err) {
  var res = err && err.response;
  var code = res && res.data && res.data.code;
  if (code === 'CLAIM_ALREADY_DECIDED') return 'CLAIM_ALREADY_DECIDED';
  if (res && res.status === 413) return 'PAYLOAD_TOO_LARGE';
  if (res && (res.status === 400 || res.status === 415 || res.status === 422)) return 'INVALID_FILE';
  if (res && res.status === 403) return 'FORBIDDEN';
  if (!res) return 'network';
  return 'generic';
}

// ── E1 «لما بضيف مطالبة جديدة محتاجها تفتح في صفحة جديدة مستقلة» ───────────
//
// Shared by the list screen and the new claim-form.vue page (they were local
// copies in claims.vue, so a change to either could silently diverge).

/** Today as YYYY-MM-DD in LOCAL time — the `max` on the service-date input.
 *  toISOString() would be UTC and, for Cairo (UTC+2/+3), rejects "today" as a
 *  future date for part of the evening. */
function todayIso(now) {
  var d = now || new Date();
  var m = String(d.getMonth() + 1);
  if (m.length < 2) m = '0' + m;
  var day = String(d.getDate());
  if (day.length < 2) day = '0' + day;
  return d.getFullYear() + '-' + m + '-' + day;
}

/** A blank claim form. */
function blankClaimForm() {
  return {
    employeeId: '',
    policyId: '',
    amount: '',
    serviceDate: '',
    provider: '',
    note: '',
  };
}

module.exports = {
  RULE_CODES: RULE_CODES,
  adjudicationView: adjudicationView,
  ATTACH_MAX_FILES: ATTACH_MAX_FILES,
  ATTACH_MAX_BYTES: ATTACH_MAX_BYTES,
  ATTACH_KINDS: ATTACH_KINDS,
  checkAttachments: checkAttachments,
  uploadPath: uploadPath,
  uploadAttachments: uploadAttachments,
  attachErrorKey: attachErrorKey,
  normalizeAmount: normalizeAmount,
  validateClaimForm: validateClaimForm,
  buildClaimPayload: buildClaimPayload,
  claimErrorKey: claimErrorKey,
  decisionReason: decisionReason,
  documentList: documentList,
  documentFilePath: documentFilePath,
  canDecide: canDecide,
  STATUS_ORDER: STATUS_ORDER,
  CLAIM_TAG: CLAIM_TAG,
  claimTag: claimTag,
  buildMap: buildMap,
  resolveName: resolveName,
  statusCounts: statusCounts,
  toCsv: toCsv,
  todayIso: todayIso,
  blankClaimForm: blankClaimForm,
};
